//
// Exports a preset file. Emits panel-headers for:
//   • EmitterPanel   (authoritative for emitter UI)
//   • TargetingPanel (presetId/mode, rangeM, headSpeedMps)
//   • SpritePanel    (sprite mode/selections/fps)
//   • ParticlesPanel (throughput, motion, lifespan, alpha/scale, blend, tint, etc.)
//   • OrbitPanel     (orbit motion around origin / per-particle)
//   • RotationPanel  (per-particle rotation)
//   • GravityPanel   (radial gravity)
//   • CollisionPanel (wall/token collision & FX)
//   • GlowPanel      (per-particle glow controls: enable/percent/intensity/reach/duration/tint)
// Also includes the legacy "parent" block for backward compatibility.
//

export default function exportPreset(
  effectName,
  effectKey,
  pxPerMeter,
  showGrid,
  parentSnapshot,
  layers
) {
  const name = effectName || "";
  const parent = parentSnapshot || {};
  const shapeSettings = parent.shapeSettings || {};
  const emitterShape = parent.emitterShape || "dot";

  // --- Rule UI (EmitterPanel) from LS
  let fireMode = "infinite";
  let ruleStyle = "infinite";
  let ruleDurationS = 2;
  try {
    fireMode = (localStorage.getItem("EmitterTool:UI:fireMode") || "infinite").toLowerCase();
  } catch {}
  try {
    ruleStyle = (localStorage.getItem("EmitterTool:UI:ruleStyle") || "infinite").toLowerCase();
  } catch {}
  try {
    const d = Number(localStorage.getItem("EmitterTool:UI:ruleDur"));
    if (Number.isFinite(d)) ruleDurationS = d;
  } catch {}

  // --- TargetingPanel from LS snapshot (kept by PresetApply on import)
  let targetingPresetId = "global";
  let targetingRangeM = 9;
  let targetingSpeed = 8;
  try {
    const m = localStorage.getItem("EmitterTool:targeting:mode");
    if (m) targetingPresetId = m;
  } catch {}
  try {
    const r = Number(localStorage.getItem("EmitterTool:targeting:rangeM"));
    if (Number.isFinite(r)) targetingRangeM = r;
  } catch {}
  try {
    const s = Number(localStorage.getItem("EmitterTool:targeting:headSpeedMps"));
    if (Number.isFinite(s)) targetingSpeed = s;
  } catch {}

  // --- SpritePanel header derived from parentSnapshot.effectSettings
  const es = (parent && parent.effectSettings) || {};
  const selectedSprite =
    parent?.selectedSprite ??
    es?.selectedSprite ??
    (Array.isArray(es?.selectedSprites) ? es.selectedSprites[0] : null);

  const selectedSprites = Array.isArray(es?.selectedSprites)
    ? es.selectedSprites.slice()
    : (selectedSprite ? [selectedSprite] : []);

  const selectedAnimFrames = Array.isArray(es?.selectedAnimFrames)
    ? es.selectedAnimFrames.slice()
    : [];

  let mode = (es?.spriteMode || "").toLowerCase();
  if (!mode) {
    if (selectedAnimFrames.length >= 2) mode = "anim";
    else if (selectedSprites.length > 1) mode = "multi";
    else mode = "single";
  }
  const animFps = Number.isFinite(+es?.animFps) ? +es.animFps : 12;

  // --- ParticlesPanel header (pull from effectSettings; fallback to panel defaults)
  const pp = {
    // Throughput
    pps: Number.isFinite(+es.pps) ? +es.pps : 100,
    maxAlive: Number.isFinite(+es.maxAlive) ? +es.maxAlive : 1000,

    // Motion
    speedMin: Number.isFinite(+es.speedMin) ? +es.speedMin : 1,
    speedMax: Number.isFinite(+es.speedMax) ? +es.speedMax : 3,

    // Lifespan mode
    lifespanMode: typeof es.lifespanMode === "string" ? es.lifespanMode : "time",

    // Lifetime (time-based)
    lifeMinS: Number.isFinite(+es.lifeMinS) ? +es.lifeMinS : 1,
    lifeMaxS: Number.isFinite(+es.lifeMaxS) ? +es.lifeMaxS : 2.5,

    // Range-based
    rangeMeters: Number.isFinite(+es.rangeMeters) ? +es.rangeMeters : 10,

    // To-target
    targetKillDistMeters: Number.isFinite(+es.targetKillDistMeters) ? +es.targetKillDistMeters : 0.25,

    // Alpha
    alphaStart: Number.isFinite(+es.alphaStart) ? +es.alphaStart : 1,
    alphaEnd: Number.isFinite(+es.alphaEnd) ? +es.alphaEnd : 0,

    // Scale
    scaleStart: Number.isFinite(+es.scaleStart) ? +es.scaleStart : 1,
    scaleEnd: Number.isFinite(+es.scaleEnd) ? +es.scaleEnd : 1,

    // Blending
    blendMode: typeof es.blendMode === "string" ? es.blendMode : "NORMAL",

    // Tint (optional)
    tintEnabled: !!es.tintEnabled,
    tintStart: typeof es.tintStart === "string" ? es.tintStart : "#ffffff",
    tintEnd: typeof es.tintEnd === "string" ? es.tintEnd : "#ffffff",
    tintEase: typeof es.tintEase === "string" ? es.tintEase : "linear",

    // Facing (kept for future hooks)
    facingMode: typeof es.facingMode === "string" ? es.facingMode : "deg_from_origin",
    facingDegFromOrigin: Number.isFinite(+es.facingDegFromOrigin) ? +es.facingDegFromOrigin : 0,
  };

  // Normalize before export
  if (pp.speedMin > pp.speedMax) [pp.speedMin, pp.speedMax] = [pp.speedMax, pp.speedMin];
  if (pp.lifeMinS > pp.lifeMaxS) [pp.lifeMinS, pp.lifeMaxS] = [pp.lifeMaxS, pp.lifeMinS];

  // --- OrbitPanel header
  const orbitDirection = (es.orbitDirection === "cw" || es.orbitDirection === "ccw") ? es.orbitDirection : "ccw";
  const orbitSpeedAbsDps = Number.isFinite(+es.orbitSpeedAbsDps) ? +es.orbitSpeedAbsDps :
                           Number.isFinite(+es.orbitSpeedDegPerS) ? Math.abs(+es.orbitSpeedDegPerS) : 90;
  const orbitSpeedDegPerS = Number.isFinite(+es.orbitSpeedDegPerS)
    ? +es.orbitSpeedDegPerS
    : (orbitDirection === "ccw" ? +orbitSpeedAbsDps : -orbitSpeedAbsDps);

  const op = {
    orbitEnabled: !!es.orbitEnabled,
    orbitDirection,
    orbitSpeedAbsDps,
    orbitSpeedDegPerS,

    orbitLockRadii: es.orbitLockRadii != null ? !!es.orbitLockRadii : true,
    orbitRadiusMinM: Number.isFinite(+es.orbitRadiusMinM) ? +es.orbitRadiusMinM : 1,
    orbitRadiusMaxM: Number.isFinite(+es.orbitRadiusMaxM) ? +es.orbitRadiusMaxM : 1,

    orbitShape: es.orbitShape === "ellipse" ? "ellipse" : "circle",
    orbitEllipseRxM: Number.isFinite(+es.orbitEllipseRxM) ? +es.orbitEllipseRxM : 1,
    orbitEllipseRyM: Number.isFinite(+es.orbitEllipseRyM) ? +es.orbitEllipseRyM : 1,
    orbitEllipseTiltDeg: Number.isFinite(+es.orbitEllipseTiltDeg) ? +es.orbitEllipseTiltDeg : 0,

    orbitStartPhaseMode: es.orbitStartPhaseMode === "aligned" ? "aligned" : "random",
    orbitStartPhaseJitterDeg: Number.isFinite(+es.orbitStartPhaseJitterDeg) ? +es.orbitStartPhaseJitterDeg : 30,

    orbitApplyMode: es.orbitApplyMode === "percent" ? "percent" : "all",
    orbitApplyPercent: Number.isFinite(+es.orbitApplyPercent) ? +es.orbitApplyPercent : 100,
  };

  if (op.orbitRadiusMinM > op.orbitRadiusMaxM) {
    const a = op.orbitRadiusMinM, b = op.orbitRadiusMaxM;
    op.orbitRadiusMinM = Math.min(a, b);
    op.orbitRadiusMaxM = Math.max(a, b);
  }
  const signedOrbit = (op.orbitDirection === "ccw" ? 1 : -1) * Math.abs(op.orbitSpeedAbsDps);
  op.orbitSpeedDegPerS = signedOrbit;

  // --- RotationPanel header
  const rotDirection =
    es.rotateDirection === "cw" || es.rotateDirection === "ccw" ? es.rotateDirection : "ccw";
  const rotAbs = Number.isFinite(+es.rotateSpeedAbsDps)
    ? +es.rotateSpeedAbsDps
    : Number.isFinite(+es.rotateSpeedDegPerS)
    ? Math.abs(+es.rotateSpeedDegPerS)
    : 90;
  const rotSigned = Number.isFinite(+es.rotateSpeedDegPerS)
    ? +es.rotateSpeedDegPerS
    : (rotDirection === "ccw" ? +rotAbs : -+rotAbs);

  const rp = {
    rotateEnabled: !!es.rotateEnabled,
    rotateDirection: rotDirection,
    rotateSpeedAbsDps: Math.abs(rotAbs),
    rotateSpeedDegPerS: rotSigned,
  };

  // --- GravityPanel header
  const gp = {
    gravityEnabled: !!es.gravityEnabled,
    gravitySpeed: Number.isFinite(+es.gravitySpeed) ? +es.gravitySpeed : 0,
    gravityLingerS: Number.isFinite(+es.gravityLingerS) ? +es.gravityLingerS : 0,
  };

  // --- CollisionPanel header
  const cp = {
    useWalls: !!(es.useWalls),
    wallResponse: typeof es.wallResponse === "string" ? es.wallResponse : "none",
    useTokenCollision: !!(es.useTokenCollision),
    tokenResponse: typeof es.tokenResponse === "string" ? es.tokenResponse : "none",

    collisionEffectEnabled: !!es.collisionEffectEnabled,
    collisionSprite: typeof es.collisionSprite === "string" ? es.collisionSprite : "",
    collisionScale: Number.isFinite(+es.collisionScale) ? +es.collisionScale : 1,
    collisionAlpha: Number.isFinite(+es.collisionAlpha) ? +es.collisionAlpha : 1,
    collisionLifespanMs: Number.isFinite(+es.collisionLifespanMs) ? +es.collisionLifespanMs : 400,
    collisionFxRotationMode: typeof es.collisionFxRotationMode === "string" ? es.collisionFxRotationMode : "none",
  };

  // --- GlowPanel header (NEW)
  const gl = {
    glowEnabled: !!es.glowEnabled,
    glowPercent: Number.isFinite(+es.glowPercent) ? Math.max(0, Math.min(100, +es.glowPercent)) : 100,
    glowIntensity: Number.isFinite(+es.glowIntensity) ? Math.max(0, Math.min(1, +es.glowIntensity)) : 1,
    glowReachM: Number.isFinite(+es.glowReachM) ? Math.max(0, +es.glowReachM) : 10,
    glowReachPx: Number.isFinite(+es.glowReachPx) ? Math.max(0, +es.glowReachPx) : 0,
    glowDurationS: Number.isFinite(+es.glowDurationS) ? Math.max(0, +es.glowDurationS) : 0.75,
    tintEnabled: !!es.tintEnabled, // note: separate from ParticlesPanel tint usage if your runtime differentiates
    tintStart: typeof es.tintStart === "string" ? es.tintStart : "#ffffff",
    tintEnd: typeof es.tintEnd === "string" ? es.tintEnd : "#ffffff",
  };

  const data = {
    name,
    effectKey,
    pxPerMeter,
    showGrid,

    // ---- Panel headers ----
    EmitterPanel: {
      shape: emitterShape,
      showShape: !!parent.showShape, // mirrored if parentSnapshot includes it
      settings: { ...shapeSettings },
      rule: { fireMode, ruleStyle, ruleDurationS },
    },

    TargetingPanel: {
      presetId: targetingPresetId, // "global" | "point" | "tokentotarget" | "direction"
      rangeM: targetingRangeM,     // number
      headSpeedMps: targetingSpeed // number (only used by tokentotarget)
    },

    SpritePanel: {
      mode,
      selectedSprite: selectedSprite || (selectedSprites[0] ?? null),
      selectedSprites,
      selectedAnimFrames,
      animFps,
    },

    ParticlesPanel: pp,
    OrbitPanel: op,
    RotationPanel: rp,
    GravityPanel: gp,
    CollisionPanel: cp,

    // NEW:
    GlowPanel: gl,

    // ---- Legacy mirror (optional, harmless) ----
    parent: parent,
    layers: Array.isArray(layers) ? layers : [],
  };

  const js = `// Exported from EmitterTool\nexport default ${JSON.stringify(data, null, 2)};\n`;
  const blob = new Blob([js], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = (name || "effect").replace(/[^\w.-]+/g, "_").toLowerCase() + ".js";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
